import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  postDefaultDirectPayment,
  moovDirectPayment,
  ERefundType,
  EntityType,
} from '@/services/direct';
import { useMutation } from '@tanstack/react-query';
import { useRefundDetails } from '../../hooks/useRefundDetails';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import Layout from '../Layout';
import SelectComponent from '../Select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { IconButton } from '../ui/IconButton';
import { Check } from 'lucide-react';
import { EClaimType } from '../../lib/types';
import { useTranslation } from 'react-i18next';
import { InfoCircledIcon } from '@radix-ui/react-icons';
import { InfoCard } from '../ui/InfoCard';

const DirectDeposit = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const minAccountNumber = 5;
  const maxAccountNumber = 17;
  const accountNumberMessage = t(`bankaccout.range`, {
    minAccountNumber,
    maxAccountNumber,
  });

  const { claimType } = useSearch({ strict: false });

  const { data } = useRefundDetails();

  const showPayeeName = data?.refundData?.isCustomPaymentTypesEnabled
    ? data?.refundData?.entity_type === EntityType.BUSINESS ||
      data?.refundData?.entity_type === EntityType.UNKNOWN
    : data?.refundData?.refund_type === ERefundType.BUSINESS;

  const formSchema = z
    .object({
      first_name: z.string().min(2).max(50).optional().or(z.literal('')),
      last_name: z.string().min(2).max(50).optional().or(z.literal('')),
      business_payee_name: z
        .string()
        .min(2)
        .max(60)
        .optional()
        .or(z.literal('')),
      email: z
        .string()
        .min(1, { message: t('Email is required') })
        .email(),
      bankDetails: z.object({
        account_number: z
          .string()
          .min(minAccountNumber, { message: accountNumberMessage })
          .max(maxAccountNumber, { message: accountNumberMessage }),
        confirm_account_number: z
          .string()
          .min(minAccountNumber, { message: accountNumberMessage })
          .max(maxAccountNumber, { message: accountNumberMessage }),
        routing_number: z
          .string()
          .min(9, {
            message: t('Please enter a 9 digit long routing number'),
          })
          .max(9, {
            message: t('Please enter a 9 digit long routing number'),
          }),
        name:
          data?.method === 'default'
            ? z
                .string()
                .min(2, {
                  message: t('Please enter a bank name'),
                })
                .max(50, {
                  message: t('Bank name must be less than 50 characters'),
                })
            : z
                .string()
                .min(2, {
                  message: t('Please enter a bank name'),
                })
                .max(50, {
                  message: t('Bank name must be less than 50 characters'),
                })
                .optional(),
        type:
          data?.method === 'moov'
            ? z
                .string()
                .min(2, { message: t('Please select a bank account type') })
                .max(50, {
                  message: t(
                    'Bank account type must be less than 50 characters',
                  ),
                })
            : z
                .string()
                .min(2, {
                  message: t('Please select a bank account type'),
                })
                .max(50, {
                  message: t(
                    'Bank account type must be less than 50 characters',
                  ),
                })
                .optional(),
      }),
    })
    .refine(
      (data) =>
        data.bankDetails.account_number ===
        data.bankDetails.confirm_account_number,
      {
        message: t('Account number does not match.'),
        path: ['bankDetails.confirm_account_number'],
      },
    );
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      business_payee_name: '',
      email: '',
      bankDetails: {
        account_number: '',
        confirm_account_number: '',
        routing_number: '',
        name: '',
        type: '',
      },
    },
  });
  const moovMutation = useMutation({
    mutationFn: moovDirectPayment,

    onSuccess: () => {
      return navigate({
        to: `/payments/thank-you`,
      });
    },
  });

  const defaultMutation = useMutation({
    mutationFn: postDefaultDirectPayment,

    onSuccess: () => {
      return navigate({
        to: `/payments/thank-you`,
      });
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const email = form.getValues('email');

    try {
      if (data?.method === 'moov' && !moovMutation.isPending) {
        const holder_name = showPayeeName
          ? data?.refundData.business_payee_name
          : `${values.first_name} ${values.last_name}`;
        const holderType = showPayeeName ? 'business' : 'individual';

        moovMutation.mutate({
          holder_name,
          account_number: values.bankDetails.account_number,
          bank_account_type: values.bankDetails.type as 'checking' | 'savings',
          routing_number: values.bankDetails.routing_number,
          contactInfo:
            data.refundData.email || email || data.refundData.phone || '',
          holder_type: holderType,
        });
      }

      if (data?.method === 'default' && !defaultMutation.isPending) {
        defaultMutation.mutate({
          account_number: values.bankDetails.account_number,
          routing_number: values.bankDetails.routing_number,
          bank_name: values.bankDetails.name ?? '',
        });
      }
    } catch {
      throw new Error(t('Failed to submit Payment details'));
    }
  };

  useEffect(() => {
    form.reset({
      first_name: data?.refundData.first_name || '',
      last_name: data?.refundData.last_name || '',
      business_payee_name: data?.refundData.business_payee_name || '',
      email: data?.refundData.email || '',
    });
  }, [data, form]);

  return (
    <Layout className='p-0'>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className='rounded-md border border-border bg-card p-6 pt-6'>
            <h6 className='mb-6 font-normal text-primary-600'>
              {t('Personal Details')}
            </h6>
            <div className='flex flex-col gap-3 pb-3 lg:flex-row lg:gap-6'>
              {showPayeeName ? (
                <FormField
                  control={form.control}
                  name='business_payee_name'
                  render={({ field }) => (
                    <FormItem className='w-full'>
                      <FormLabel>{t('Payee Name')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Business Name'
                          {...field}
                          disabled
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <>
                  <FormField
                    control={form.control}
                    name='first_name'
                    render={({ field }) => (
                      <FormItem className='w-full'>
                        <FormLabel>{t('First Name')}</FormLabel>
                        <FormControl>
                          <Input placeholder='John' {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='last_name'
                    render={({ field }) => (
                      <FormItem className='w-full'>
                        <FormLabel>{t('Last Name')}</FormLabel>
                        <FormControl>
                          <Input placeholder='Doe' {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </div>
            <div className='flex flex-col gap-4 lg:gap-4'>
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel>
                      {t('E-mail')}
                      {claimType === EClaimType.LETTER && (
                        <span className='ml-1 text-red-500'>*</span>
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder='Email'
                        {...field}
                        disabled={claimType !== EClaimType.LETTER}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {claimType === EClaimType.LETTER && (
                <InfoCard>{t('ach.email.help.text')}</InfoCard>
              )}
            </div>
            <hr className='my-5 border-dashed lg:my-10' />
            <h6 className='mb-2 font-normal text-primary-600'>
              {t('Bank details')}
            </h6>

            <div className='mb-3 flex items-center gap-2 rounded-lg border border-dashed border-blue-500 bg-blue-100 p-4 dark:bg-blue-800'>
              <InfoCircledIcon className='h-6 w-6' />{' '}
              <p className=''>
                {t(
                  'Please make sure to confirm the name on your bank account matches the name above.',
                )}
              </p>
            </div>
            <div className='flex flex-col gap-3 lg:grid lg:grid-cols-2 lg:gap-6'>
              <FormField
                control={form.control}
                name='bankDetails.account_number'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel>{t('Bank Account Number')}</FormLabel>
                    <FormControl>
                      <Input placeholder='123...' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='bankDetails.confirm_account_number'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel>{t('Confirm Bank Account Number')}</FormLabel>
                    <FormControl>
                      <Input placeholder='123...' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='bankDetails.routing_number'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel className='font-medium'>
                      {t('Bank Routing Number')}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder='123...' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {data?.method === 'moov' ? (
                <FormField
                  control={form.control}
                  name='bankDetails.type'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='font-medium'>
                        {t('Bank Account Type')}
                      </FormLabel>
                      <SelectComponent onValueChange={field.onChange} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <FormField
                  control={form.control}
                  name='bankDetails.name'
                  render={({ field }) => (
                    <FormItem className='w-full'>
                      <FormLabel>{t('Bank Name')}</FormLabel>
                      <FormControl>
                        <Input placeholder='Bank Name' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            <div className='mt-6 flex justify-end gap-2.5'>
              <IconButton
                isLoading={moovMutation.isPending || defaultMutation.isPending}
                Icon={Check}
                type='submit'
                variant='success'
              >
                {t('Claim Payment')}
              </IconButton>
            </div>
          </div>
        </form>
      </Form>
    </Layout>
  );
};

export default DirectDeposit;
